import { useState } from 'react'
import { abrirDocumento } from '../../api/queries/documentos'
import { mensagemDoErro } from '../../api/erros'
import { Botao } from '../../components/Botao'
import { IconeDocumento } from '../../components/IconesDeDocumento'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { DocumentoDoContrato } from '../../types/documentos'
import { tamanhoLegivel } from './formato'
import { SeloDaLeitura } from './SeloDaLeitura'

/**
 * Um documento da lista: o que ele é, se a consulta o lê, e as duas ações.
 *
 * O título é o nome que a fiscalização deu (é ele que aparece nas citações); o nome do arquivo vai
 * menor, embaixo, porque é o que a pessoa reconhece da pasta dela. Os oito primeiros caracteres do
 * hash ficam no `title`, para quem quiser conferir com `sha256sum` sem a tela se encher de técnica.
 *
 * @param props o documento, se a pessoa pode retirar, e o que fazer ao pedir a retirada
 * @returns o cartão
 */
export function CartaoDoDocumento({
  documento,
  podeRetirar,
  aoRetirar,
}: {
  documento: DocumentoDoContrato
  podeRetirar: boolean
  aoRetirar: () => void
}) {
  const t = TEXTOS.documentos
  const [erro, setErro] = useState<string | null>(null)
  const retirado = Boolean(documento.retiradoEm)

  async function abrir() {
    setErro(null)
    try {
      await abrirDocumento(documento.id)
    } catch (falha) {
      setErro(mensagemDoErro(falha))
    }
  }

  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
      <div className="flex min-w-0 gap-3">
        <span
          className={
            retirado
              ? 'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400'
              : 'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-niteroi-50 text-niteroi-700'
          }
        >
          <IconeDocumento />
        </span>
        <div className="min-w-0 space-y-1">
          <p className="text-xs text-slate-500">{documento.tipoRotulo}</p>
          <h3 className={retirado ? 'font-semibold text-slate-500 line-through' : 'font-semibold text-slate-900'}>
            {documento.titulo}
          </h3>
          <p className="truncate text-xs text-slate-500" title={`SHA-256: ${documento.sha256}`}>
            {documento.nomeOriginal} · {tamanhoLegivel(documento.tamanhoBytes)} · enviado por{' '}
            {documento.enviadoPorNome ?? '—'} em {dataHora(documento.enviadoEm)}
          </p>
          {retirado ? (
            <p className="max-w-prose text-xs text-slate-600">
              Retirado por {documento.retiradoPorNome ?? '—'} em {dataHora(documento.retiradoEm!)}:{' '}
              <span className="italic">“{documento.motivoDaRetirada}”</span>
            </p>
          ) : (
            <SeloDaLeitura documento={documento} />
          )}
          {erro && (
            <p role="alert" className="text-xs text-red-800">
              {erro}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 sm:pt-5">
        <Botao tamanho="pequeno" onClick={() => void abrir()}>
          {t.abrir}
        </Botao>
        {podeRetirar && !retirado && (
          <Botao tamanho="pequeno" aparencia="texto" onClick={aoRetirar}>
            {t.retirar}
          </Botao>
        )}
      </div>
    </li>
  )
}
